import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { RoundedTabs } from '@components';
import { LOCAL_STORAGE_KEYS } from '@constants/storage';
import { Event, sendMixpanelEvent } from '@core/analytics/mixpanel';
import { getLocalStorageItem } from '@utils/storage';
import { TabPill } from 'types/tabs';

import { LeadDetailTabId, mainTabs } from '../constants/leadDetails';
import { useGetLeadDetailsQuery } from '../queries/LeadManagementQuery';
import { LeadActivityPanelProps } from '../types/leadActivity';
import { isMetaSyncedLead } from '../utils/metaIntegrationHelpers';
import {
  ActivitiesTab,
  AttachmentsTab,
  FormResponseTab,
  NotesTab,
  TimelineTab
} from './lead-activity-panel';

const LeadActivityPanel: React.FC<LeadActivityPanelProps> = ({
  className = '',
  leadId,
  analyticsData
}) => {
  const [selectedTabId, setSelectedTabId] = useState<LeadDetailTabId>(
    LeadDetailTabId.ACTIVITIES
  );
  const { leadDetails } = analyticsData ?? {};
  const { currentOrg } = useSelector((state: any) => state.user);
  const organizationId =
    getLocalStorageItem(LOCAL_STORAGE_KEYS.ORGANIZATION_ID) || '';

  const { data: lead } = useGetLeadDetailsQuery(
    { organizationId, leadId },
    { skip: !organizationId || !leadId }
  );

  const visibleTabs = useMemo(
    () =>
      isMetaSyncedLead(lead)
        ? mainTabs
        : mainTabs.filter((tab) => tab.id !== LeadDetailTabId.FORM_RESPONSE),
    [lead]
  );

  const selectedTab =
    visibleTabs.find((tab) => tab.id === selectedTabId) ?? visibleTabs[0];

  const onTabChange = (tab: TabPill) => {
    if (selectedTab.id === tab.id) return;
    if (tab.id === LeadDetailTabId.ACTIVITIES) {
      sendMixpanelEvent(Event.LEAD_ACTIVITIES_CLICKED, {
        org_id: currentOrg?.id,
        org_name: currentOrg?.name,
        lead_id: leadId,
        lead_name: leadDetails?.leadName,
        lead_phone: leadDetails?.leadPhone
      });
    }
    setSelectedTabId(tab.id as LeadDetailTabId);
  };

  const renderTabContent = () => {
    switch (selectedTab.id) {
      case LeadDetailTabId.ACTIVITIES:
        return <ActivitiesTab leadId={leadId} analyticsData={analyticsData} />;
      case LeadDetailTabId.NOTES:
        return <NotesTab leadId={leadId} />;
      case LeadDetailTabId.TIMELINE:
        return <TimelineTab leadId={leadId} />;
      case LeadDetailTabId.ATTACHMENTS:
        return <AttachmentsTab leadId={leadId} />;
      case LeadDetailTabId.FORM_RESPONSE:
        return <FormResponseTab leadId={leadId} />;
      default:
        return <ActivitiesTab leadId={leadId} analyticsData={analyticsData} />;
    }
  };

  return (
    <div className={`h-fit w-full rounded-xl ${className}`}>
      <RoundedTabs
        tabs={visibleTabs}
        selectedTab={selectedTab}
        onTabSelect={onTabChange}
        borderClassName="!rounded-t-2xl"
        buttonClassName="!rounded-t-2xl !min-w-0 whitespace-nowrap md:!min-w-0"
      />
      <div className="rounded-xl rounded-tl-none border border-grey30 bg-white">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default LeadActivityPanel;
